import preact from "../js/lib/preact";
import { toID, toRoomid, toUserid, Dex, PSUtils } from "./battle-dex";
import type { ID } from "./battle-dex-data";
import { BattleLog } from "./battle-log";
import { PSLoginServer } from "./client-connection";
import { PSBackground } from "./client-core";
import {
	PS, PSRoom, Config, type PSRoomFocusOptions, type RoomOptions, type PSLoginState, type RoomID,
	type TimestampOptions, type BattleLayoutPreference,
} from "./client-main";
import { type BattleRoom } from "./panel-battle";
import { ChatUserList, PSTextarea, type ChatRoom } from "./panel-chat";
import { PSRoomPanel, PSPanelWrapper, PSView } from "./panels";
import { PSHeader } from "./panel-topbar";
import { getClientLanguage, trGlobalRankLabel, trOfficialRankLabel, trUI } from "./client-i18n";

const WARNING_SECONDS = 5;
const BATTLE_LAYOUT_LABELS: Record<BattleLayoutPreference, string> = {
	'side-by-side': 'Side-by-side, controls below',
	'side-by-side-overlay': 'Side-by-side, overlay controls',
	'top-and-bottom': 'Top-and-bottom, controls below',
	'top-and-bottom-overlay': 'Top-and-bottom, overlay controls',
	'scrolling': 'Scrolling, controls below',
	'scrolling-overlay': 'Scrolling, overlay controls',
};
function battleLayoutLabel(id: BattleLayoutPreference) {
	return trUI(BATTLE_LAYOUT_LABELS[id]);
}

/** Fallback Remix Icon por nombre de medalla (si el servidor aún no envía `icon`). */
const BADGE_REMIX_FALLBACK: { [name: string]: string } = {
	Desarrollador: 'ri-code-s-slash-line',
	Migajero: 'ri-emotion-laugh-line',
	Administrador: 'ri-shield-star-line',
	'Beta Tester': 'ri-bug-line',
	TorneoCoffee: 'ri-trophy-line',
	XoXeo: 'ri-fire-line',
	Diseñador: 'ri-palette-line',
	Aureo: 'ri-vip-crown-line',
	Moderador: 'ri-shield-check-line',
	'Staff de Clanes': 'ri-flag-2-line',
	'Periodista de The Eternity Times': 'ri-newspaper-line',
	'Maestro PokéRogue': 'ri-gamepad-line',
	'Director de The Eternity Times': 'ri-briefcase-4-line',
	DJ: 'ri-headphone-line',
};

function sanitizeRemixIconClass(raw: string | undefined | null): string | null {
	const icon = (raw || '').trim().toLowerCase();
	if (!/^ri-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(icon)) return null;
	return icon;
}

function resolveBadgeRemixIcon(icon: string | undefined, name: string): string | null {
	return sanitizeRemixIconClass(icon) || sanitizeRemixIconClass(BADGE_REMIX_FALLBACK[name]) || null;
}

/** true solo cuando la fuente Remix Icon está lista; si no, el perfil muestra `badge.img`. */
let remixIconFontReady = false;
let remixIconFontCheckStarted = false;
const remixIconFontWaiters: Array<() => void> = [];

function ensureRemixIconFontReady(onReady: () => void) {
	if (remixIconFontReady) {
		onReady();
		return;
	}
	remixIconFontWaiters.push(onReady);
	if (remixIconFontCheckStarted) return;
	remixIconFontCheckStarted = true;
	const finish = (ok: boolean) => {
		remixIconFontReady = ok;
		if (!ok) return;
		const waiters = remixIconFontWaiters.splice(0);
		for (const w of waiters) w();
	};
	try {
		if (typeof document === 'undefined' || !document.fonts?.load) {
			finish(false);
			return;
		}
		void document.fonts.load('16px remixicon').then(() => {
			finish(document.fonts.check('16px remixicon'));
		}).catch(() => finish(false));
	} catch {
		finish(false);
	}
}

type ClientOrigin = 'custom' | 'hub' | 'mixed';

function clientOriginFromSym(sym: string): ClientOrigin | '' {
	if (sym === '◆') return 'custom';
	if (sym === '◇') return 'hub';
	if (sym === '◈') return 'mixed';
	return '';
}

function clientOriginLabelOf(origin: ClientOrigin | '' | undefined): string {
	if (origin === 'custom') return 'Cliente Eternity';
	if (origin === 'hub') return 'Sim / hub (psim.us)';
	if (origin === 'mixed') return 'Cliente Eternity + sim';
	return '';
}

function UserPopupIconBtn(props: {
	icon: string,
	label: string,
	href?: string,
	cmd?: string,
	disabled?: boolean,
}) {
	return <button
		type="button"
		class="et-userpopup__iconbtn"
		data-href={props.disabled ? undefined : props.href}
		data-cmd={props.disabled ? undefined : props.cmd}
		disabled={props.disabled}
		aria-label={props.label}
		title={props.label}
	>
		<i class={props.icon} aria-hidden></i>
	</button>;
}

type UserPopupRoomKind = 'chat' | 'private' | 'battle';

function UserPopupRoomChip(props: {
	roomid: string,
	kind: UserPopupRoomKind,
	rank: string,
	label: string,
	joined: boolean,
	hint?: string,
}) {
	return <button
		type="button"
		class={
			'et-userpopup__chip et-userpopup__chip--' + props.kind +
			(props.joined ? ' et-userpopup__chip--here' : '')
		}
		data-href={props.roomid}
		aria-label={props.hint || props.label}
	>
		{props.rank ? <span class="et-userpopup__chiprank" aria-hidden>{props.rank}</span> : null}
		<span class="et-userpopup__chipname">{props.label}</span>
	</button>;
}

/** A partir de N salas se resume en una línea; si no, se ven las pastillas. */
const USER_POPUP_ROOM_PREVIEW = 5;

class UserPopupRoomSection extends preact.Component<{
	kind: UserPopupRoomKind,
	label: string,
	chips: preact.ComponentChild[],
}> {
	override state = { expanded: false };
	toggle = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setState({ expanded: !this.state.expanded });
	};
	override render() {
		const chips = this.props.chips;
		if (!chips.length) return null;
		const many = chips.length > USER_POPUP_ROOM_PREVIEW;
		const expanded = !many || this.state.expanded;
		return <div class={
			'et-userpopup__roomsec et-userpopup__roomsec--' + this.props.kind +
			(many ? ' et-userpopup__roomsec--many' : '') +
			(expanded ? ' et-userpopup__roomsec--open' : '')
		}>
			{many ? (
				<button type="button" class="et-userpopup__roomsum" onClick={this.toggle}>
					<span class="et-userpopup__roomsec-label">{this.props.label}</span>
					<span class="et-userpopup__roomsum-meta">
						<span class="et-userpopup__roomsum-n">{chips.length}</span>
						<span class="et-userpopup__roomsum-caret" aria-hidden>{expanded ? '▴' : '▾'}</span>
					</span>
				</button>
			) : (
				<div class="et-userpopup__roomsec-label">{this.props.label}</div>
			)}
			{expanded && <div class="et-userpopup__chips">{chips}</div>}
		</div>;
	}
}

/** Prefer userdetails.clientOrigin; fallback to ◆/◇/◈ still present in room userlists. */
function resolveClientOrigin(
	userid: string,
	fromDetails?: ClientOrigin | '' | null,
): ClientOrigin | '' {
	if (fromDetails === 'custom' || fromDetails === 'hub' || fromDetails === 'mixed') {
		return fromDetails;
	}
	for (const roomid in PS.rooms) {
		const room = PS.rooms[roomid] as ChatRoom | undefined;
		const identity = room?.users?.[userid as ID];
		if (!identity) continue;
		const base = identity.endsWith('@!') ? identity.slice(0, -2) : identity;
		const m = /([◆◇◈])$/.exec(base);
		if (!m) continue;
		const origin = clientOriginFromSym(m[1]);
		if (origin) return origin;
	}
	return '';
}

/**
 * User popup
 */

export class UserRoom extends PSRoom {
	override readonly classType = 'user';
	userid!: ID;
	name!: string;
	isSelf!: boolean;
	constructor(options: RoomOptions) {
		super(options);
		const userid = (this.id.split('-')[1] || '') as ID;
		this.setName(options.args?.username as string || userid);
	}
	setName(name: string) {
		this.name = name;
		this.userid = toID(name);
		this.isSelf = (this.userid === PS.user.userid);
		if (/[a-zA-Z0-9]/.test(this.name.charAt(0))) this.name = ' ' + this.name;
		this.update(null);
		if (this.userid) PS.send(`/cmd userdetails ${this.userid}`);
	}
}

export class StatusEditor extends preact.Component {
	declare state: { addingStatus?: boolean, statusChanged?: boolean };
	fetchStatus() {
		if (!PS.user.userid) return;
		PS.mainmenu.makeQuery('userdetails', PS.user.userid).then(() => this.forceUpdate());
	}
	currentStatus() {
		let status = PS.mainmenu.userdetailsCache[PS.user.userid]?.status || '';
		if (status.startsWith('!')) status = status.slice(1);
		return status.replace(/^\((?:Busy|Idle)\) ?/, '');
	}
	statusLabel() {
		const status = PS.mainmenu.userdetailsCache[PS.user.userid]?.status || '';
		return /^!?(\((?:Busy|Idle)\))/.exec(status)?.[1] || '';
	}
	statusInput() {
		return this.base!.querySelector<HTMLTextAreaElement>('textarea[name=statustext]');
	}
	addStatus = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setState({ addingStatus: true }, () => this.focusStatus());
	};
	focusStatus = (ev?: Event) => {
		ev?.preventDefault();
		ev?.stopImmediatePropagation();
		const input = this.statusInput();
		input?.focus();
		input?.select();
	};
	handleStatusInput = (ev: Event) => {
		const changed = (ev.currentTarget as HTMLTextAreaElement).value !== this.currentStatus();
		if (changed !== !!this.state.statusChanged) this.setState({ statusChanged: changed });
	};
	handleStatusKeyDown = (ev: KeyboardEvent) => {
		if (ev.key === 'Escape') {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			this.revertStatus();
		}
	};
	revertStatus = (ev?: Event) => {
		ev?.preventDefault();
		ev?.stopImmediatePropagation();
		const input = this.statusInput();
		if (input) {
			input.value = input.defaultValue;
			input.dispatchEvent(new Event('input'));
		}
		this.setState({ addingStatus: false, statusChanged: false });
	};
	submitStatus = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const status = (this.statusInput()?.value || '').trim();
		PS.send(status ? `/noreply /status ${status}` : `/noreply /clearstatus`);
		// optimistic update; the requery below will catch any server-side filtering
		const details = PS.mainmenu.userdetailsCache[PS.user.userid];
		if (details) {
			const awayMarkers = /^!?(?:\((?:Busy|Idle)\) ?)?/.exec(details.status || '')![0];
			details.status = awayMarkers + status;
		}
		this.setState({ addingStatus: false, statusChanged: false });
		this.fetchStatus();
	};
	override render() {
		const curStatus = this.currentStatus();
		const label = this.statusLabel();
		if (!curStatus && !this.state.addingStatus) {
			return <div>
				{label && <span class="userstatus" style="display:inline">{trUI(label)}</span>} {}
				<button class="button small" onClick={this.addStatus}>{trUI("Add status")}</button>
			</div>;
		}
		return <form onSubmit={this.submitStatus} class="statusform">
			{label && <span class="userstatus" style="display:inline">{trUI(label)}</span>} {}
			<PSTextarea
				class="userstatus"
				key={curStatus} singleLine subtle name="statustext" defaultValue={curStatus}
				onInput={this.handleStatusInput} onKeyDown={this.handleStatusKeyDown}
			/> {}
			{this.state.statusChanged || this.state.addingStatus ? (
				<>
					<button class="button" type="submit">
						<strong>{curStatus ? trUI("Change") : trUI("Add")}</strong>
					</button> {}
					<button class="button" type="button" onClick={this.revertStatus}>
						{curStatus ? trUI("Revert") : trUI("Cancel")}
					</button>
				</>
			) : (
				<button class="button small" type="button" onClick={this.focusStatus} aria-label={trUI("Edit")}>
					<i class="fa fa-pencil" aria-hidden></i>
				</button>
			)}
		</form>;
	}
}

class UserPanel extends PSRoomPanel<UserRoom> {
	static readonly id = 'user';
	static readonly routes = ['user-*', 'viewuser-*', 'users'];
	static readonly Model = UserRoom;
	static readonly location = 'popup';

	override componentDidMount() {
		super.componentDidMount();
		ensureRemixIconFontReady(() => this.forceUpdate());
	}
	override componentDidUpdate() {
		super.componentDidUpdate();
		this.syncPopupBackground();
	}

	cssPopupBg(raw: string | undefined): string {
		if (!raw) return '';
		const v = String(raw).trim();
		if (!v || v.length > 500 || /[;{}\\]/.test(v)) return '';
		if (/^https?:\/\//i.test(v)) {
			try {
				const u = new URL(v);
				if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
				if (/[\s)"']/.test(v)) return '';
				return `url("${v}")`;
			} catch {
				return '';
			}
		}
		if (/^(?:repeating-)?(?:linear|radial|conic)-gradient\(/i.test(v) && v.length < 500 && !/\burl\s*\(/i.test(v)) {
			return v;
		}
		return '';
	}
	syncPopupBackground() {
		const root = this.base as HTMLElement | null;
		if (!root) return;
		const popup = root.classList?.contains('ps-popup') ? root : root.closest?.('.ps-popup');
		if (!popup) return;
		const room = this.props.room;
		const details = room.userid ? PS.mainmenu.userdetailsCache[room.userid] : null;
		const css = this.cssPopupBg(details?.popupbg);
		const wantHas = !!css;
		const wantAnim = !!(css && /gradient\(/i.test(css) && !/\d+(\.\d+)?%\s+\d+(\.\d+)?%/.test(css));
		const wantConic = !!(wantAnim && /conic-gradient/i.test(css));
		popup.classList.toggle('et-userpopup-hasbg', wantHas);
		popup.classList.toggle('et-userpopup-anim', wantAnim);
		popup.classList.toggle('et-userpopup-anim-conic', wantConic);
		if (css) {
			if (popup.style.getPropertyValue('--et-popup-bg') !== css) {
				popup.style.setProperty('--et-popup-bg', css);
			}
		} else if (popup.style.getPropertyValue('--et-popup-bg')) {
			popup.style.removeProperty('--et-popup-bg');
		}
	}

	renderUser() {
		const room = this.props.room;
		if (!room.userid) return null;
		const user = PS.mainmenu.userdetailsCache[room.userid] || {
			userid: room.userid, name: room.name.slice(1), avatar: '[loading]',
		};
		if (!user.avatar) {
			// offline; server doesn't know the actual username
			user.name = room.name;
		}
		const hideInteraction = room.id.startsWith('viewuser-');

		const group = PS.server.getGroup(room.name);
		const rawGroupName = group.name || '';
		const globalGroup = PS.server.getGroup(user.group);
		const rawGlobalName = globalGroup.name || '';
		let groupName: preact.ComponentChild = rawGroupName && rawGroupName !== rawGlobalName ?
			trOfficialRankLabel(rawGroupName) : null;
		let globalGroupName: preact.ComponentChild = rawGlobalName ? trGlobalRankLabel(rawGlobalName) : null;
		let customGroup = toID(user.customgroup) !== toID(rawGlobalName ? `Global ${rawGlobalName}` : rawGroupName) ?
			user.customgroup : '';

		if (group.type === 'punishment') {
			groupName = <span style="color:#777777">{groupName}</span>;
		}
		if (globalGroup.type === 'punishment') {
			globalGroupName = <span style="color:#777777">{globalGroupName}</span>;
		}

		let roomsList: preact.ComponentChild = null;
		if (user.rooms) {
			const battlebuf: preact.ComponentChild[] = [];
			const chatbuf: preact.ComponentChild[] = [];
			const privatebuf: preact.ComponentChild[] = [];
			for (const rawRoomid in user.rooms) {
				if (rawRoomid === 'global') continue;
				const curRoom = user.rooms[rawRoomid];
				const rank = !/[A-Za-z0-9]/.test(rawRoomid.charAt(0)) ? rawRoomid.charAt(0) : '';
				const roomid = toRoomid(rawRoomid);
				const joined = roomid in PS.rooms;

				if (roomid.startsWith('battle-')) {
					const p1 = (curRoom.p1 || '').slice(1);
					const p2 = (curRoom.p2 || '').slice(1);
					const ownBattle = (
						PS.user.userid === toUserid(p1) || PS.user.userid === toUserid(p2)
					);
					const chip = <UserPopupRoomChip
						roomid={roomid}
						kind="battle"
						rank={rank}
						label={roomid.slice(7)}
						joined={ownBattle || joined}
						hint={`${p1 || '?'} v. ${p2 || '?'}`}
					/>;
					if (curRoom.isPrivate) privatebuf.push(chip);
					else battlebuf.push(chip);
				} else {
					const chip = <UserPopupRoomChip
						roomid={roomid}
						kind={curRoom.isPrivate ? 'private' : 'chat'}
						rank={rank}
						label={roomid}
						joined={joined}
					/>;
					if (curRoom.isPrivate) privatebuf.push(chip);
					else chatbuf.push(chip);
				}
			}
			if (battlebuf.length || chatbuf.length || privatebuf.length) {
				roomsList = <div class="et-userpopup__rooms rooms">
					<UserPopupRoomSection kind="chat" label={trUI("Chatrooms:").replace(/:\s*$/, '')} chips={chatbuf} />
					<UserPopupRoomSection kind="private" label={trUI("Private rooms:").replace(/:\s*$/, '')} chips={privatebuf} />
					<UserPopupRoomSection kind="battle" label={trUI("Battles:").replace(/:\s*$/, '')} chips={battlebuf} />
				</div>;
			}
		} else if (user.rooms === false) {
			roomsList = <div class="et-userpopup__rooms"><strong class="offline">{trUI("OFFLINE")}</strong></div>;
		}

		const isSelf = user.userid === PS.user.userid;
		let away = false;
		let status = null;
		if (user.status) {
			away = user.status.startsWith('!');
			status = away ? user.status.slice(1) : user.status;
		}

		let actions: preact.ComponentChild = null;
		if (!hideInteraction) {
			if (isSelf) {
				actions = <div class="et-userpopup__actions">
					<UserPopupIconBtn icon="ri-chat-3-line" label={trUI("Chat Self")} href="dm-" />
					<UserPopupIconBtn icon="ri-pencil-line" label={trUI("Change name")} href="login" />
					<UserPopupIconBtn icon="ri-logout-box-r-line" label={trUI("Log out")} cmd="/logout" />
				</div>;
			} else {
				const guest = !PS.user.named;
				actions = <div class="et-userpopup__actions">
					<UserPopupIconBtn
						icon="ri-add-line" label={trUI("Options")}
						href={`useroptions-${user.userid}-${room.parentRoomid || ''}`} disabled={guest}
					/>
				</div>;
			}
		}

		const avatar = user.avatar !== '[loading]' ? Dex.resolveAvatar(`${user.avatar || 'unknown'}`) : null;
		const clientOrigin = resolveClientOrigin(user.userid, user.clientOrigin);
		const clientOriginLabel = clientOriginLabelOf(clientOrigin);
		return <div class={'userdetails et-userpopup' + (isSelf ? ' et-userpopup--self' : '')}>
			<div class="et-userpopup__head">
				{avatar && (room.isSelf ? (
					<img src={avatar} class="trainersprite yours" data-href="avatars" />
				) : (
					<img src={avatar} class="trainersprite" />
				))}
				<div class="et-userpopup__id">
					<div class="et-userpopup__idtop">
						{actions}
						<strong><a
							href={`//${Config.routes.users}/${user.userid}`} target="_blank"
							style={`color: ${away ? '#888888' : BattleLog.usernameColor(user.userid)}`}
						>
							{user.name}
						</a></strong>
					</div>
					{isSelf && !hideInteraction ? (
						<StatusEditor />
					) : status ? (
						<div class="userstatus">{status}</div>
					) : null}
					{groupName && <div class="usergroup roomgroup">{groupName}</div>}
					{globalGroupName && <div class="usergroup globalgroup">{globalGroupName}</div>}
					{customGroup && <div class="usergroup globalgroup">{customGroup}</div>}
					{!!clientOrigin && clientOriginLabel && (
						<div
							class="eternity-client-origin usergroup"
							data-origin={clientOrigin}
							title={clientOriginLabel}
						>
							<span
								class="eternity-client-sym"
								data-origin={clientOrigin}
								aria-hidden
							/>
							{clientOriginLabel}
						</div>
					)}
					{!!user.badges?.length && <div class="userbadges">
						{user.badges.map(badge => {
							const iconClass = resolveBadgeRemixIcon(badge.icon, badge.name);
							const useRemix = !!(iconClass && remixIconFontReady);
							return <span class="userbadge" key={badge.name} title={badge.name} aria-label={badge.name}>
								{useRemix ? (
									<i class={`${iconClass} userbadge-icon`} aria-hidden></i>
								) : (
									<img src={badge.img} width={20} height={20} alt="" />
								)}
							</span>;
						})}
					</div>}
				</div>
			</div>
			{!hideInteraction && roomsList}
		</div>;
	}

	lookup = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const room = this.props.room;
		const username = this.base!.querySelector<HTMLInputElement>('input[name=username]')?.value;
		room.setName(username || '');
	};
	maybeReset = (ev: Event) => {
		const room = this.props.room;
		const username = this.base!.querySelector<HTMLInputElement>('input[name=username]')?.value;
		if (toID(username) !== room.userid) {
			room.setName('');
		}
	};

	override render() {
		const room = this.props.room;
		const showLookup = room.id === 'users';

		return <PSPanelWrapper room={room} width={288}><div class="pad">
			{showLookup && <form onSubmit={this.lookup} style={{ minWidth: 0, maxWidth: '100%' }}>
				<label class="label">
					{trUI("Username:")} {}
					<input type="search" name="username" class="textbox autofocus" onInput={this.maybeReset} onChange={this.maybeReset} />
				</label>
				{!room.userid && <p class="buttonbar">
					<button type="submit" class="button"><strong>{trUI("Look up")}</strong></button> {}
					<button name="closeRoom" class="button">{trUI("Close")}</button>
				</p>}
				{!!room.userid && <hr />}
			</form>}

			{this.renderUser()}
		</div></PSPanelWrapper>;
	}
}

const USER_PUNISHMENTS = {
	mute: { label: 'Mute', duration: '7 minutes', scope: 'room' },
	hourmute: { label: 'Hourmute', duration: '1 hour', scope: 'room' },
	ban: { label: 'Ban', duration: '2 days', scope: 'room' },
	weekban: { label: 'Weekban', duration: '1 week', scope: 'room' },
	lock: { label: 'Lock', duration: '2 days', scope: 'global' },
	weeklock: { label: 'Weeklock', duration: '1 week', scope: 'global' },
	namelock: { label: 'Namelock', duration: '2 days', scope: 'global' },
} as const;
type UserPunishment = keyof typeof USER_PUNISHMENTS;

function userOptionPunishState(targetUser: ID, targetRoom: ChatRoom | null) {
	const roomIdent = targetRoom?.users[targetUser] || '';
	const roomSym = roomIdent.charAt(0);
	const details = PS.mainmenu.userdetailsCache[targetUser];
	let globalSym = details?.group || '';
	if (!globalSym) {
		for (const id in PS.rooms) {
			const ident = (PS.rooms[id] as ChatRoom | undefined)?.users?.[targetUser];
			if (!ident) continue;
			const sym = ident.charAt(0);
			if (PS.server.getGroup(sym).type === 'punishment') {
				globalSym = sym;
				break;
			}
		}
	}
	const roomName = (PS.server.getGroup(roomSym).name || '').toLowerCase();
	const globalName = (PS.server.getGroup(globalSym).name || '').toLowerCase();
	const namelocked = globalSym === '\u2716' || roomSym === '\u2716' || globalName.includes('namelock');
	return {
		muted: roomSym === '!' || roomName.includes('muted'),
		namelocked,
		locked: !namelocked && (
			globalSym === '\u203d' || roomSym === '\u203d' ||
			globalName.includes('locked') || roomName.includes('locked')
		),
	};
}

function UserOptBtn(props: {
	icon?: string,
	label: preact.ComponentChild,
	href?: string,
	cmd?: string,
	cmdpreview?: string,
	value?: string,
	onClick?: (ev: Event) => void,
	kind?: 'undo' | 'danger',
	/** Room id for Modlog button (`data-modlog-room`). */
	modlogRoom?: string,
}) {
	return <button
		type="button"
		class={'button et-useropts__btn' + (props.kind ? ` et-useropts__btn--${props.kind}` : '')}
		data-href={props.href}
		data-cmd={props.cmd}
		data-cmdpreview={props.cmdpreview}
		data-modlog-room={props.modlogRoom}
		value={props.value}
		onClick={props.onClick}
	>
		{props.icon ? <i class={props.icon} aria-hidden></i> : null}
		<span>{props.label}</span>
	</button>;
}

class UserOptionsPanel extends PSRoomPanel {
	static readonly id = 'useroptions';
	static readonly routes = ['useroptions-*'];
	static readonly location = 'popup';
	static readonly noURL = true;
	getTargets() {
		const [, targetUser, targetRoomid] = PSUtils.splitFirst(this.props.room.id, '-', 2);
		let targetRoom = (targetRoomid ? PS.rooms[targetRoomid] : null) as ChatRoom | null || null;
		if (targetRoom?.type !== 'chat') targetRoom = targetRoom?.getParent() as ChatRoom;
		if (targetRoom?.type !== 'chat') targetRoom = targetRoom?.getParent() as ChatRoom;
		if (targetRoom?.type !== 'chat') targetRoom = null;
		if (targetRoom?.pmTarget) targetRoom = null;
		return { targetUser: targetUser as ID, targetRoomid: targetRoomid as RoomID, targetRoom };
	}

	handleCommand = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const command = (ev.currentTarget as HTMLButtonElement).getAttribute('data-cmdpreview');
		if (!command) return PS.alert(trUI("Room not found"), { parentElem: ev.currentTarget as HTMLElement });
		this.send(command);
	};
	/** Open server modlog panel for the target user (room or global). */
	openModlog = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const button = ev.currentTarget as HTMLButtonElement;
		const { targetUser, targetRoom } = this.getTargets();
		const modRoom = button.getAttribute('data-modlog-room') || targetRoom?.id;
		if (!modRoom || !targetUser) {
			return PS.alert(trUI("Room not found"), { parentElem: button });
		}
		// Explicit room= + user= (same shape as helptickets buttons); works even if
		// the outbound message is attributed to a non-chat popup room id.
		this.send(`/modlog room=${modRoom}, user=${targetUser}`);
	};
	send(command: string) {
		const { targetRoom } = this.getTargets();
		if (targetRoom) {
			targetRoom.send(command);
		} else {
			// Prefer any joined chat room so /modlog (and similar) keep a real room
			// context; fall back to a bare send instead of opening a DM.
			const chat = Object.values(PS.rooms).find(
				r => r && r.type === 'chat' && !(r as ChatRoom).pmTarget
			);
			if (chat) {
				chat.send(command);
			} else {
				PS.send(command);
			}
		}
		this.close();
	}

	openPunishment = (ev: Event) => {
		const button = ev.currentTarget as HTMLButtonElement;
		const punishment = button.value as UserPunishment;
		ev.preventDefault();
		ev.stopImmediatePropagation();
		if (!(punishment in USER_PUNISHMENTS)) return;

		const { targetUser, targetRoom } = this.getTargets();
		const action = USER_PUNISHMENTS[punishment];
		const scope = action.scope === 'room' ? ` ${trUI("in")} ${targetRoom?.title || '???'}` : '';
		PS.prompt(
			`${trUI(action.label)} ${targetUser}${scope} ${trUI("for")} ${trUI(action.duration)}?`,
			{ okButton: trUI(action.label), parentElem: button, label: trUI("Reason (optional):") }
		).then(reason => {
			if (reason === null) return;
			reason = reason.trim();
			this.send(`/${punishment} ${targetUser}${reason ? `, ${reason}` : ''}`);
		});
	};

	openUndo = (ev: Event) => {
		const button = ev.currentTarget as HTMLButtonElement;
		const action = button.value;
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const labels: { [cmd: string]: string } = {
			unmute: 'Unmute',
			unlock: 'Unlock',
			unnamelock: 'Unnamelock',
		};
		if (!labels[action]) return;
		const { targetUser, targetRoom } = this.getTargets();
		const label = trUI(labels[action]);
		if (action === 'unmute' || action === 'unlock') {
			this.send(`/${action} ${targetUser}`);
			return;
		}
		const scope = action === 'unmute' ? ` ${trUI("in")} ${targetRoom?.title || '???'}` : '';
		PS.prompt(
			`${label} ${targetUser}${scope}?`,
			{ okButton: label, parentElem: button, label: trUI("Reason (optional):") }
		).then(reason => {
			if (reason === null) return;
			reason = reason.trim();
			this.send(`/${action} ${targetUser}${reason ? `, ${reason}` : ''}`);
		});
	};

	isIgnoringUser = (userid: string) => {
		const ignoring = PS.prefs.ignore || {};
		if (ignoring[userid] === 1) return true;
		return false;
	};

	override componentDidMount() {
		super.componentDidMount();
		const { targetUser, targetRoom } = this.getTargets();
		if (targetRoom) {
			this.subscriptions.push(targetRoom.subscribe(() => this.forceUpdate()));
		}
		if (targetUser) {
			PS.mainmenu.makeQuery('userdetails', targetUser).then(() => this.forceUpdate());
		}
	}

	override render() {
		const room = this.props.room;
		const { targetUser, targetRoom } = this.getTargets();
		const userRoomGroup = targetRoom?.users[PS.user.userid]?.charAt(0);
		const roomGroupOrder = PS.server.getGroup(userRoomGroup).order;
		const globalGroupOrder = PS.server.getGroup(PS.user.group).order;
		const canMute = !!targetRoom && roomGroupOrder <= PS.server.getGroup('%').order;
		const canBan = !!targetRoom && roomGroupOrder <= PS.server.getGroup('@').order;
		const canLock = globalGroupOrder <= PS.server.getGroup('%').order;
		const punish = userOptionPunishState(targetUser, targetRoom);

		return <PSPanelWrapper room={room} width={260}><div class="pad et-useropts">
			<div class="et-useropts__row">
				<UserOptBtn
					icon="ri-boxing-line" label={trUI("Challenge")}
					href={`challenge-${targetUser}`}
				/>
				<UserOptBtn
					icon="ri-chat-3-line" label={trUI("Chat")}
					href={`dm-${targetUser}`}
				/>
			</div>
			<div class="et-useropts__row">
				<UserOptBtn
					icon="ri-user-add-line" label={trUI("Add friend")}
					cmd={`/dm ${targetUser}, /friend add ${targetUser}`}
				/>
			</div>
			<div class="et-useropts__row">
				{this.isIgnoringUser(targetUser) ? (
					<UserOptBtn
						icon="ri-eye-line" label={trUI("Unignore")}
						cmdpreview={`/unignore ${targetUser}`} onClick={this.handleCommand}
					/>
				) : (
					<UserOptBtn
						icon="ri-eye-off-line" label={trUI("Ignore")}
						cmdpreview={`/ignore ${targetUser}`} onClick={this.handleCommand}
					/>
				)}
				<UserOptBtn
					icon="ri-alarm-warning-line" label={trUI("Report")}
					href={`view-help-request-report-user-${targetUser}`}
				/>
			</div>
			{(canMute || canBan) && <div class="et-useropts__sec">
				<div class="et-useropts__sec-title">{trUI("Moderate in")} {targetRoom?.title}</div>
				{canMute && (punish.muted ? (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-volume-up-line" label={trUI("Unmute")} kind="undo"
							value="unmute" cmdpreview={`/unmute ${targetUser}`} onClick={this.openUndo}
						/>
					</div>
				) : (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-volume-mute-line"
							label={<>{trUI("Mute")} <small>7m</small></>}
							value="mute" cmdpreview={`/mute ${targetUser}`} onClick={this.openPunishment}
						/>
						<UserOptBtn
							icon="ri-volume-mute-line"
							label={<>{trUI("Hourmute")} <small>1h</small></>}
							value="hourmute" cmdpreview={`/hourmute ${targetUser}`} onClick={this.openPunishment}
						/>
					</div>
				))}
				{canBan && <div class="et-useropts__row">
					<UserOptBtn
						icon="ri-forbid-line"
						label={<>{trUI("Ban")} <small>2d</small></>}
						value="ban" kind="danger" cmdpreview={`/ban ${targetUser}`} onClick={this.openPunishment}
					/>
					<UserOptBtn
						icon="ri-forbid-line"
						label={<>{trUI("Weekban")} <small>1w</small></>}
						value="weekban" kind="danger" cmdpreview={`/weekban ${targetUser}`} onClick={this.openPunishment}
					/>
				</div>}
				<div class="et-useropts__row">
					<UserOptBtn
						icon="ri-file-list-3-line" label={trUI("Modlog")}
						modlogRoom={targetRoom!.id}
						cmdpreview={`/modlog room=${targetRoom!.id}, user=${targetUser}`}
						onClick={this.openModlog}
					/>
				</div>
			</div>}
			{canLock && <div class="et-useropts__sec">
				<div class="et-useropts__sec-title">{trUI("Global moderation")}</div>
				{punish.locked ? (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-lock-unlock-line" label={trUI("Unlock")} kind="undo"
							value="unlock" cmdpreview={`/unlock ${targetUser}`} onClick={this.openUndo}
						/>
					</div>
				) : (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-lock-line"
							label={<>{trUI("Lock")} <small>2d</small></>}
							value="lock" cmdpreview={`/lock ${targetUser}`} onClick={this.openPunishment}
						/>
						<UserOptBtn
							icon="ri-lock-line"
							label={<>{trUI("Weeklock")} <small>1w</small></>}
							value="weeklock" cmdpreview={`/weeklock ${targetUser}`} onClick={this.openPunishment}
						/>
					</div>
				)}
				{punish.namelocked ? (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-user-follow-line" label={trUI("Unnamelock")} kind="undo"
							value="unnamelock" cmdpreview={`/unnamelock ${targetUser}`} onClick={this.openUndo}
						/>
					</div>
				) : (
					<div class="et-useropts__row">
						<UserOptBtn
							icon="ri-user-unfollow-line"
							label={<>{trUI("Namelock")} <small>2d</small></>}
							value="namelock" cmdpreview={`/namelock ${targetUser}`} onClick={this.openPunishment}
						/>
					</div>
				)}
				<div class="et-useropts__row">
					<UserOptBtn
						icon="ri-file-list-3-line" label={trUI("Global modlog")}
						modlogRoom="global"
						cmdpreview={`/modlog room=global, user=${targetUser}`}
						onClick={this.openModlog}
					/>
				</div>
			</div>}
		</div></PSPanelWrapper>;
	}
}

class UserListPanel extends PSRoomPanel {
	static readonly id = 'userlist';
	static readonly routes = ['userlist'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;
	override render() {
		const room = this.props.room;
		const parentRoom = room.getParent() as ChatRoom;
		if (parentRoom.type !== 'chat' && parentRoom.type !== 'battle') {
			throw new Error(`UserListPanel: ${room.id} is not a chat room`);
		}

		return <PSPanelWrapper room={room} width={280}><div class="pad">
			<ChatUserList room={parentRoom} static />
		</div></PSPanelWrapper>;
	}
}

class VolumePanel extends PSRoomPanel {
	static readonly id = 'volume';
	static readonly routes = ['volume'];
	static readonly location = 'popup';

	setVolume = (e: Event) => {
		const slider = e.currentTarget as HTMLInputElement;
		PS.prefs.set(slider.name as 'effectvolume', Number(slider.value));
		this.forceUpdate();
	};
	setMute = (e: Event) => {
		const checkbox = e.currentTarget as HTMLInputElement;
		PS.prefs.set('mute', !!checkbox.checked);
		PS.update();
	};
	override componentDidMount() {
		super.componentDidMount();
		this.subscriptions.push(PS.prefs.subscribe(() => {
			this.forceUpdate();
		}));
	}
	override render() {
		const room = this.props.room;
		return <PSPanelWrapper room={room}><div class="pad">
			<h3>{trUI("Volume")}</h3>
			<p class="volume">
				<label class="optlabel">
					{trUI("Effects:")} <span class="value">{!PS.prefs.mute && PS.prefs.effectvolume ? `${PS.prefs.effectvolume}%` : `-`}</span>
				</label>
				{PS.prefs.mute ?
					<em>{trUI("(muted)")}</em> :
					<input
						type="range" min="0" max="100" step="1" name="effectvolume" value={PS.prefs.effectvolume}
						onChange={this.setVolume} onInput={this.setVolume} onKeyUp={this.setVolume}
					/>}
			</p>
			<p class="volume">
				<label class="optlabel">
					{trUI("Music:")} <span class="value">{!PS.prefs.mute && PS.prefs.musicvolume ? `${PS.prefs.musicvolume}%` : `-`}</span>
				</label>
				{PS.prefs.mute ?
					<em>{trUI("(muted)")}</em> :
					<input
						type="range" min="0" max="100" step="1" name="musicvolume" value={PS.prefs.musicvolume}
						onChange={this.setVolume} onInput={this.setVolume} onKeyUp={this.setVolume}
					/>}
			</p>
			<p class="volume">
				<label class="optlabel">
					{trUI("Notifications:")} {}
					<span class="value">{!PS.prefs.mute && PS.prefs.notifvolume ? `${PS.prefs.notifvolume}%` : `-`}</span>
				</label>
				{PS.prefs.mute ?
					<em>{trUI("(muted)")}</em> :
					<input
						type="range" min="0" max="100" step="1" name="notifvolume" value={PS.prefs.notifvolume}
						onChange={this.setVolume} onInput={this.setVolume} onKeyUp={this.setVolume}
					/>}
			</p>
			<p>
				<label class="checkbox">
					<input type="checkbox" name="mute" checked={PS.prefs.mute} onChange={this.setMute} /> {trUI("Mute all")}
				</label>
			</p>
		</div></PSPanelWrapper>;
	}
}

class OptionsPanel extends PSRoomPanel {
	static readonly id = 'options';
	static readonly routes = ['options'];
	static readonly location = 'modal-popup';

	declare state: { graphicsOpen: boolean, chatOpen: boolean };
	constructor() {
		super(...arguments as any);
		this.state = {
			graphicsOpen: OptionsPanel.readSectionOpen('graphics', true),
			/** On short viewports, start Chat collapsed so the menu fits ~768–900px. */
			chatOpen: OptionsPanel.readSectionOpen('chat', typeof window !== 'undefined' ? window.innerHeight >= 900 : true),
		};
	}

	static readSectionOpen(key: string, fallback: boolean): boolean {
		try {
			const v = localStorage.getItem(`eternity-options-section-${key}`);
			if (v === '0') return false;
			if (v === '1') return true;
		} catch {}
		return fallback;
	}
	static writeSectionOpen(key: string, open: boolean) {
		try {
			localStorage.setItem(`eternity-options-section-${key}`, open ? '1' : '0');
		} catch {}
	}

	override componentDidMount() {
		super.componentDidMount();
		this.subscribeTo(PS.user);
		this.subscriptions.push(PS.prefs.subscribe(key => {
			if (key === 'serversettings' || key === null) this.forceUpdate();
		}));
		PS.mainmenu.makeQuery('userdetails', PS.user.userid).then(() => this.forceUpdate());
	}
	toggleSection = (key: 'graphics' | 'chat') => {
		const stateKey = key === 'graphics' ? 'graphicsOpen' : 'chatOpen';
		const next = !this.state[stateKey];
		OptionsPanel.writeSectionOpen(key, next);
		this.setState({ [stateKey]: next });
	};
	setTheme = (e: Event) => {
		const theme = (e.currentTarget as HTMLSelectElement).value as 'light' | 'dark' | 'system';
		PS.prefs.set('theme', theme);
		this.forceUpdate();
	};
	setLayout = (e: Event) => {
		const layout = (e.currentTarget as HTMLSelectElement).value;
		switch (layout) {
		case '':
			PS.prefs.set('onepanel', null);
			PS.rightPanel ||= PS.rooms['rooms'] || null;
			break;
		case 'onepanel':
			PS.prefs.set('onepanel', true);
			break;
		case 'vertical':
			PS.prefs.set('onepanel', 'vertical');
			break;
		}
		PS.update();
	};
	setChatroomTimestamp = (ev: Event) => {
		const timestamp = (ev.currentTarget as HTMLSelectElement).value as TimestampOptions;
		PS.prefs.set('timestamps', { ...PS.prefs.timestamps, chatrooms: timestamp || undefined });
	};
	setPMsTimestamp = (ev: Event) => {
		const timestamp = (ev.currentTarget as HTMLSelectElement).value as TimestampOptions;
		PS.prefs.set('timestamps', { ...PS.prefs.timestamps, pms: timestamp || undefined });
	};

	handleOnChange = (ev: Event) => {
		let elem = ev.currentTarget as HTMLInputElement;
		let setting = elem.name;
		let value = elem.checked;
		switch (setting) {
		case 'blockPMs': {
			PS.prefs.set('serversettings', { ...PS.prefs.serversettings, blockPMs: value });
			PS.send(value ? '/blockpms' : '/unblockpms');
			break;
		}
		case 'blockChallenges': {
			PS.prefs.set('serversettings', { ...PS.prefs.serversettings, blockChallenges: value });
			PS.send(value ? '/blockchallenges' : '/unblockchallenges');
			break;
		}
		case 'bwgfx': {
			PS.prefs.set('bwgfx', value);
			Dex.loadSpriteData(value || PS.prefs.noanim ? 'bw' : 'xy');
			this.forceUpdate();
			break;
		}
		case 'language': {
			PS.prefs.set('serversettings', { ...PS.prefs.serversettings, language: elem.value });
			PS.send(`/language ${elem.value}`);
			this.forceUpdate();
			break;
		}
		case 'tournaments': {
			PS.prefs.set(setting, elem.value as 'hide' | 'notify' | 'nonotify');
			break;
		}
		case 'refreshprompt':
		case 'noanim':
		case 'nopastgens':
		case 'noselfhighlight':
		case 'leavePopupRoom':
		case 'inchatpm':
			PS.prefs.set(setting, value);
			this.forceUpdate();
			break;
		}
	};

	override render() {
		const room = this.props.room;
		const serverSettings = PS.prefs.serversettings;
		const language = getClientLanguage();
		const { graphicsOpen, chatOpen } = this.state;
		const autoLayoutLabel = window.innerWidth < 700 || window.innerHeight < 430 ?
			trUI("Automatic (Vertical tabs)") :
			window.innerWidth < 900 ? trUI("Automatic (Single panel)") :
			trUI("Two panels (if wide enough)");
		return <PSPanelWrapper room={room} width={340} className="et-options-popup"><div class="pad et-options">
			<div class="et-options__profile">
				<img
					class="trainersprite yours et-options__avatar" width="40" height="40"
					src={Dex.resolveAvatar(`${PS.user.avatar}`)} data-href="avatars"
				/>
				<div class="et-options__profile-main">
					<strong class="et-options__name">{PS.user.name}</strong>
					<StatusEditor />
				</div>
			</div>
			<div class="et-options__btnrow">
				<button class="button" data-href="avatars">{trUI("Avatar...")}</button>
				{PS.user.named && (PS.user.registered?.userid === PS.user.userid ?
					<button class="button" data-href="changepassword">{trUI("Password...")}</button> :
					<button class="button" data-href="register">{trUI("Register")}</button>)}
			</div>

			<details class="et-options__sec" open={graphicsOpen}>
				<summary class="et-options__sum" onClick={e => {
					e.preventDefault();
					this.toggleSection('graphics');
				}}>
					<h3>{trUI("Graphics")}</h3>
				</summary>
				{graphicsOpen ? <div class="et-options__body">
					<label class="optlabel et-options__row">{trUI("Theme:")} <select
						name="theme" class="select" onChange={this.setTheme} value={PS.prefs.theme || 'light'}
					>
						<option value="light">{trUI("Light")}</option>
						<option value="dark">{trUI("Dark theme")}</option>
						<option value="system">{trUI("Match system theme")}</option>
					</select></label>
					<label class="optlabel et-options__row">{trUI("Layout:")} <select
						name="layout" class="select" onChange={this.setLayout}
						value={PS.prefs.onepanel === true ? 'onepanel' : PS.prefs.onepanel || ''}
					>
						<option value="">{autoLayoutLabel}</option>
						<option value="onepanel">{trUI("Single panel")}</option>
						<option value="vertical">{trUI("Vertical tabs")}</option>
					</select></label>
					<label class="optlabel et-options__row">
						{trUI("Background:")} <button class="button" data-href="changebackground">
							{trUI("Change Background")}
						</button>
					</label>
					<div class="et-options__checks">
						<label class="checkbox"><input
							name="noanim" checked={PS.prefs.noanim || false} type="checkbox" onChange={this.handleOnChange}
						/>{trUI("Disable animations")}</label>
						<label class="checkbox"><input
							name="bwgfx" checked={PS.prefs.bwgfx || false} type="checkbox" onChange={this.handleOnChange}
						/>{trUI("Use 2D sprites instead of 3D models")}</label>
						<label class="checkbox"><input
							name="nopastgens" checked={PS.prefs.nopastgens || false} type="checkbox" onChange={this.handleOnChange}
						/>{trUI("Use modern sprites for past generations")}</label>
					</div>
				</div> : null}
			</details>

			<details class="et-options__sec" open={chatOpen}>
				<summary class="et-options__sum" onClick={e => {
					e.preventDefault();
					this.toggleSection('chat');
				}}>
					<h3>{trUI("Chat")}</h3>
				</summary>
				{chatOpen ? <div class="et-options__body">
					<div class="et-options__checks">
						<label class="checkbox"><input
							name="blockPMs" checked={!!serverSettings.blockPMs} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Block DMs")}</label>
						<label class="checkbox"><input
							name="blockChallenges" checked={!!serverSettings.blockChallenges} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Block challenges")}</label>
						<label class="checkbox"><input
							name="inchatpm" checked={PS.prefs.inchatpm || false} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Show DMs in chatrooms")}</label>
						<label class="checkbox"><input
							name="noselfhighlight" checked={PS.prefs.noselfhighlight || false} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Do not highlight when your name is said in chat")}</label>
						<label class="checkbox"><input
							name="leavePopupRoom" checked={PS.prefs.leavePopupRoom || false} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Confirm before leaving a room")}</label>
						<label class="checkbox"><input
							name="refreshprompt" checked={PS.prefs.refreshprompt || false} type="checkbox" onChange={this.handleOnChange}
						/> {trUI("Confirm before refreshing")}</label>
					</div>
					<label class="optlabel et-options__row">
						{trUI("Language:")} {}
						<select name="language" onChange={this.handleOnChange} class="select" value={language}>
							<option value="english">English</option>
							<option value="german">Deutsch</option>
							<option value="spanish">Español</option>
							<option value="french">Français</option>
							<option value="italian">Italiano</option>
							<option value="dutch">Nederlands</option>
							<option value="portuguese">Português</option>
							<option value="turkish">Türkçe</option>
							<option value="hindi">हिंदी</option>
							<option value="japanese">日本語</option>
							<option value="simplifiedchinese">简体中文</option>
							<option value="traditionalchinese">中文</option>
						</select>
					</label>
					<label class="optlabel et-options__row">
						{trUI("Tournaments:")} <select
							name="tournaments" class="select" onChange={this.handleOnChange} value={PS.prefs.tournaments || 'notify'}
						>
							<option value="notify">{trUI("Always notify")}</option>
							<option value="nonotify">{trUI("Notify when joined")}</option>
							<option value="hide">{trUI("Hide")}</option>
						</select>
					</label>
					<label class="optlabel et-options__row">{trUI("Timestamps:")} <select
						name="layout" class="select" onChange={this.setChatroomTimestamp} value={PS.prefs.timestamps.chatrooms || ''}
					>
						<option value="">{trUI("Off")}</option>
						<option value="minutes">[HH:MM]</option>
						<option value="seconds">[HH:MM:SS]</option>
					</select></label>
					<label class="optlabel et-options__row">{trUI("Timestamps in DMs:")} <select
						name="layout" class="select" onChange={this.setPMsTimestamp} value={PS.prefs.timestamps.pms || ''}
					>
						<option value="">{trUI("Off")}</option>
						<option value="minutes">[HH:MM]</option>
						<option value="seconds">[HH:MM:SS]</option>
					</select></label>
					<label class="optlabel et-options__row">
						{trUI("Chat preferences:")} {}
						<button class="button" data-href="chatformatting">{trUI("Text formatting...")}</button>
					</label>
				</div> : null}
			</details>

			<div class="et-options__foot">
				{PS.user.named ? <div class="buttonbar et-options__btnrow">
					<button class="button" data-href="login"><i class="fa fa-pencil" aria-hidden></i> {trUI("Change name")}</button>
					<button class="button" data-cmd="/logout"><i class="fa fa-power-off" aria-hidden></i> {trUI("Log out")}</button>
				</div> : <div class="buttonbar et-options__btnrow">
					<button class="button" data-href="login"><i class="fa fa-pencil" aria-hidden></i> {trUI("Choose name")}</button>
				</div>}
			</div>
		</div></PSPanelWrapper>;
	}
}

class GooglePasswordBox extends preact.Component<{ name: string }> {
	override componentDidMount() {
		window.gapiCallback = (response: any) => {
			PS.user.changeNameWithPassword(this.props.name, response.credential, { needsGoogle: true });
		};

		PS.user.gapiLoaded = true;
		const script = document.createElement('script');
		script.async = true;
		script.src = 'https://accounts.google.com/gsi/client';
		document.getElementsByTagName('head')[0].appendChild(script);
	}
	override render() {
		return <div class="google-password-box">
			<div
				id="g_id_onload" data-client_id="912270888098-jjnre816lsuhc5clj3vbcn4o2q7p4qvk.apps.googleusercontent.com"
				data-context="signin" data-ux_mode="popup" data-callback="gapiCallback" data-auto_prompt="false"
			></div>
			<div
				class="g_id_signin" data-type="standard" data-shape="pill" data-theme="filled_blue" data-text="continue_with"
				data-size="large" data-logo_alignment="left" data-auto_select="true" data-itp_support="true"
				style="width:fit-content;margin:0 auto"
			>[{trUI("loading Google log-in button")}]</div>
		</div>;
	}
}

class LoginPanel extends PSRoomPanel {
	static readonly id = 'login';
	static readonly routes = ['login'];
	static readonly location = 'modal-popup';
	declare state: { passwordShown?: boolean };

	override componentDidMount() {
		super.componentDidMount();
		this.subscriptions.push(PS.user.subscribe(args => {
			if (args) {
				if (args.success) {
					this.close();
					return;
				}
				this.props.room.args = args;
				setTimeout(() => this.focus(), 1);
			}
			this.forceUpdate();
		}));
	}
	getUsername() {
		const loginName = PS.user.loggingIn || this.props.room.args?.name as string;
		if (loginName) return loginName;

		const input = this.base?.querySelector<HTMLInputElement>('input[name=username]');
		if (input && !input.disabled) {
			return input.value;
		}
		return PS.user.named ? PS.user.name : '';
	}
	handleSubmit = (ev: Event) => {
		ev.preventDefault();
		const passwordBox = this.base!.querySelector<HTMLInputElement>('input[name=password]');
		if (passwordBox) {
			PS.user.changeNameWithPassword(this.getUsername(), passwordBox.value);
		} else {
			PS.user.changeName(this.getUsername());
		}
	};
	update = () => {
		this.forceUpdate();
	};
	override focus(_options?: PSRoomFocusOptions) {
		const passwordBox = this.base!.querySelector<HTMLInputElement>('input[name=password]');
		const usernameBox = this.base!.querySelector<HTMLInputElement>('input[name=username]');
		(passwordBox || usernameBox)?.select();
	}
	reset = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.props.room.args = null;
		this.forceUpdate();
	};
	handleShowPassword = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setState({ passwordShown: !this.state.passwordShown });
	};
	override render() {
		const room = this.props.room;
		const loginState = room.args as PSLoginState;
		return <PSPanelWrapper room={room} width={280}><div class="pad">
			<h3>{trUI("Log in")}</h3>
			<form onSubmit={this.handleSubmit}>
				{loginState?.error && <p class="error">{loginState.error}</p>}
				<p><label class="label">
					{trUI("Username:")} <small class="preview" style={`color:${BattleLog.usernameColor(toID(this.getUsername()))}`}>({trUI("color")})</small>
					<input
						class="textbox" type="text" name="username"
						onInput={this.update} onChange={this.update} autocomplete="username"
						value={this.getUsername()} disabled={!!PS.user.loggingIn || !!loginState?.name}
					/>
				</label></p>
				{PS.user.named && !loginState && <p>
					<small>{trUI('(Others will be able to see your name change. To change name privately, use "Log out")')}</small>
				</p>}
				{loginState?.needsPassword && <p>
					<i class="fa fa-level-up fa-rotate-90" aria-hidden></i> <strong>{trUI("if you registered this name:")}</strong>
					<label class="label">
						{trUI("Password:")} {}
						<input
							class="textbox" type={this.state.passwordShown ? 'text' : 'password'} name="password"
							autocomplete="current-password" style="width:173px"
						/>
						<button
							type="button" onClick={this.handleShowPassword} aria-label={trUI("Show password")}
							class="button" style="float:right;margin:-21px 0 10px;padding: 2px 6px"
						><i class="fa fa-eye" aria-hidden></i></button>
					</label>
				</p>}
				{loginState?.needsGoogle && <>
					<p><i class="fa fa-level-up fa-rotate-90" aria-hidden></i> <strong>{trUI("if you registered this name:")}</strong></p>
					<p><GooglePasswordBox name={this.getUsername()} /></p>
				</>}
				<p class="buttonbar">
					{PS.user.loggingIn ? (
						<button disabled class="cur">{trUI("Logging in...")}</button>
					) : loginState?.needsPassword ? (
						<>
							<button type="submit" class="button"><strong>{trUI("Log in")}</strong></button> {}
							<button type="button" onClick={this.reset} class="button">{trUI("Cancel")}</button>
						</>
					) : loginState?.needsGoogle ? (
						<button type="button" onClick={this.reset} class="button">{trUI("Cancel")}</button>
					) : (
						<>
							<button type="submit" class="button"><strong>{trUI("Choose name")}</strong></button> {}
							<button type="button" name="closeRoom" class="button">{trUI("Cancel")}</button>
						</>
					)} {}
				</p>
				{loginState?.name && <div>
					<p>
						<i class="fa fa-level-up fa-rotate-90" aria-hidden></i> <strong>{trUI("if not:")}</strong>
					</p>
					<p style={{ maxWidth: '210px', margin: '0 auto' }}>
						{trUI("This is someone else's account. Sorry.")}
					</p>
					<p class="buttonbar">
						<button class="button" onClick={this.reset}>{trUI("Try another name")}</button>
					</p>
				</div>}
			</form>
		</div></PSPanelWrapper>;
	}
}

class AvatarsPanel extends PSRoomPanel {
	static readonly id = 'avatars';
	static readonly routes = ['avatars'];
	static readonly location = 'modal-popup';

	override render() {
		const room = this.props.room;

		const avatars: [number, string][] = [];
		for (let i = 1; i <= 293; i++) {
			if (i === 162 || i === 168) continue;
			avatars.push([i, window.BattleAvatarNumbers?.[i] || `${i}`]);
		}

		return <PSPanelWrapper room={room} width={1210}><div class="pad">
			<label class="optlabel"><strong>{trUI("Choose an avatar or")} </strong>
				<button class="button" data-cmd="/close"> {trUI("Cancel")}</button>
			</label>
			<div class="avatarlist">
				{avatars.map(([i, avatar]) => (
					<button
						data-cmd={`/closeand /avatar ${avatar}`} title={`/avatar ${avatar}`}
						class={`option pixelated${avatar === PS.user.avatar ? ' cur' : ''}`}
						style={`background-position: -${((i - 1) % 16) * 80 + 1}px -${Math.floor((i - 1) / 16) * 80 + 1}px`}
					></button>
				))}
			</div>
			<div style="clear:left"></div>
			<p><button class="button" data-cmd="/close">{trUI("Cancel")}</button></p>
		</div></PSPanelWrapper>;
	}
}

class BattleForfeitPanel extends PSRoomPanel {
	static readonly id = 'forfeit';
	static readonly routes = ['forfeitbattle'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	override render() {
		const room = this.props.room;
		const battleRoom = room.getParent() as BattleRoom;

		return <PSPanelWrapper room={room} width={480}><div class="pad">
			<p>{trUI("Forfeiting makes you lose the battle. Are you sure?")}</p>
			<p>
				<button data-cmd="/closeand /inopener /closeand /forfeit" class="button"><strong>{trUI("Forfeit and close")}</strong></button> {}
				<button data-cmd="/closeand /inopener /forfeit" class="button">{trUI("Just forfeit")}</button> {}
				{battleRoom.battle && !battleRoom.battle.rated && <button type="button" data-href="replaceplayer" class="button">
					{trUI("Replace player")}
				</button>} {}
				<button type="button" data-cmd="/close" class="button">
					{trUI("Cancel")}
				</button>
			</p>
		</div></PSPanelWrapper>;
	}
}

class ReplacePlayerPanel extends PSRoomPanel {
	static readonly id = 'replaceplayer';
	static readonly routes = ['replaceplayer'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	handleReplacePlayer = (ev: Event) => {
		const room = this.props.room;
		const battleRoom = room.getParent()?.getParent() as BattleRoom;
		const newPlayer = this.base?.querySelector<HTMLInputElement>("input[name=newplayer]")?.value;
		if (!newPlayer?.length) return battleRoom.errorReply(trUI("Enter player's name"));
		if (battleRoom.battle.ended) return battleRoom.errorReply(trUI("Cannot replace player, battle has already ended."));
		let playerSlot = battleRoom.battle.p1.id === PS.user.userid ? "p1" : "p2";
		battleRoom.send('/leavebattle');
		battleRoom.send(`/addplayer ${newPlayer}, ${playerSlot}`);
		this.close();
		ev.preventDefault();
	};

	override render() {
		const room = this.props.room;

		return <PSPanelWrapper room={room} width={480}><div class="pad">
			<form onSubmit={this.handleReplacePlayer}>
				<p>{trUI("Replacement player's name:")}</p>
				<p>
					<input name="newplayer" class="textbox autofocus" />
				</p>
				<p class="buttonbar">
					<button type="submit" class="button">
						<strong>{trUI("Replace")}</strong>
					</button> {}
					<button type="button" data-cmd="/close" class="button">
						{trUI("Cancel")}
					</button>
				</p>
			</form>
		</div></PSPanelWrapper>;
	}
}

class ChangePasswordPanel extends PSRoomPanel {
	static readonly id = "changepassword";
	static readonly routes = ["changepassword"];
	static readonly location = "modal-popup";
	static readonly noURL = true;

	declare state: { errorMsg: string };

	handleChangePassword = (ev: Event) => {
		ev.preventDefault();
		let oldpassword = this.base?.querySelector<HTMLInputElement>('input[name=oldpassword]')?.value;
		let password = this.base?.querySelector<HTMLInputElement>('input[name=password]')?.value;
		let cpassword = this.base?.querySelector<HTMLInputElement>('input[name=cpassword]')?.value;
		if (!oldpassword?.length ||
			!password?.length ||
			!cpassword?.length) return this.setState({ errorMsg: trUI("All fields are required") });
		if (password !== cpassword) return this.setState({ errorMsg: trUI("Passwords do not match") });
		PSLoginServer.query("changepassword", {
			oldpassword,
			password,
			cpassword,
		}).then(data => {
			if (data?.actionerror) return this.setState({ errorMsg: data?.actionerror });
			PS.alert(trUI("Your password was successfully changed!"));

		}).catch(err => {
			console.error(err);
			this.setState({ errorMsg: err.message });
		});

		this.setState({ errorMsg: '' });
	};

	override render() {
		const room = this.props.room;

		return <PSPanelWrapper room={room} width={280}><div class="pad">
			<form onSubmit={this.handleChangePassword}>
				{ !!this.state.errorMsg?.length && <p>
					<b class="message-error"> {this.state.errorMsg}</b>
				</p> }
				<p>{trUI("Change your password:")}</p>
				<p>
					<label class="label">
						{trUI("Username:")} {}
						<input name="username" value={PS.user.name} readOnly={true} autocomplete="username" class="textbox disabled" />
					</label>
				</p>
				<p>
					<label class="label">
						{trUI("Old password:")} {}
						<input name="oldpassword" type="password" autocomplete="current-password" class="textbox autofocus" />
					</label>
				</p>
				<p>
					<label class="label">
						{trUI("New password:")} {}
						<input name="password" type="password" autocomplete="new-password" class="textbox" />
					</label>
				</p>
				<p>
					<label class="label">
						{trUI("New password (confirm):")} {}
						<input name="cpassword" type="password" autocomplete="new-password" class="textbox" />
					</label>
				</p>
				<p class="buttonbar">
					<button type="submit" class="button">
						<strong>{trUI("Change password")}</strong>
					</button> {}
					<button type="button" data-cmd="/close" class="button">{trUI("Cancel")}</button>
				</p>
			</form>
		</div>
		</PSPanelWrapper>;
	}
}

class RegisterPanel extends PSRoomPanel {
	static readonly id = "register";
	static readonly routes = ["register"];
	static readonly location = "modal-popup";
	static readonly noURL = true;
	static readonly rightPopup = true;

	declare state: { errorMsg: string };

	handleRegisterUser = (ev: Event) => {
		ev.preventDefault();
		let captcha = this.base?.querySelector<HTMLInputElement>('input[name=captcha]')?.value;
		let password = this.base?.querySelector<HTMLInputElement>('input[name=password]')?.value;
		let cpassword = this.base?.querySelector<HTMLInputElement>('input[name=cpassword]')?.value;
		if (!captcha?.length ||
			!password?.length ||
			!cpassword?.length) return this.setState({ errorMsg: trUI("All fields are required") });
		if (password !== cpassword) return this.setState({ errorMsg: trUI("Passwords do not match") });
		PSLoginServer.query("register", {
			captcha,
			password,
			cpassword,
			username: PS.user.name,
			challstr: PS.user.challstr,
		}).then(data => {
			if (data?.actionerror) this.setState({ errorMsg: data?.actionerror });
			if (data?.curuser?.loggedin) {
				let name = data.curuser.username;
				PS.user.registered = { name, userid: toID(name) };
				if (data?.assertion) PS.user.handleAssertion(name, data?.assertion);
				this.close();
				PS.alert(trUI("You have been successfully registered."));
			}
		}).catch(err => {
			console.error(err);
			this.setState({ errorMsg: err.message });
		});

		this.setState({ errorMsg: '' });
	};

	override render() {
		const room = this.props.room;

		return <PSPanelWrapper room={room} width={280}><div class="pad">
			<form onSubmit={this.handleRegisterUser}>
				{ !!this.state.errorMsg?.length && <p>
					<b class="message-error"> {this.state.errorMsg}</b>
				</p> }
				<p>{trUI("Register your account:")}</p>
				<p>
					<label class="label">
						{trUI("Username:")} {}
						<input name="name" value={PS.user.name} readOnly={true} autocomplete="username" class="textbox disabled" />
					</label>
				</p>
				<p>
					<label class="label">
						{trUI("Password:")} {}
						<input name="password" type="password" autocomplete="new-password" class="textbox autofocus" />
					</label>
				</p>
				<p>
					<label class="label">
						{trUI("Password (confirm):")} {}
						<input name="cpassword" type="password" autocomplete="new-password" class="textbox" />
					</label>
				</p>
				<p>
					<label class="label"><img
						src="https://play.pokemonshowdown.com/sprites/gen5ani/pikachu.gif"
						alt={trUI("An Electric-type mouse that is the mascot of the Pokémon franchise.")}
					/></label>
				</p>
				<p>
					<label class="label">
						{trUI("What is this pokemon?")} {}
						<input name="captcha" class="textbox" />
					</label>
				</p>
				<p class="buttonbar">
					<button type="submit" class="button"><strong>{trUI("Register")}</strong></button> {}
					<button type="button" data-cmd="/close" class="button">{trUI("Cancel")}</button>
				</p>
			</form>

		</div>
		</PSPanelWrapper>;
	}
}

class BackgroundListPanel extends PSRoomPanel {
	static readonly id = 'changebackground';
	static readonly routes = ['changebackground'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;
	static handleDrop(ev: DragEvent) {
		const files = ev.dataTransfer?.files;
		if (files?.[0]?.type?.startsWith('image/')) {
			// It's an image file, try to set it as a background
			BackgroundListPanel.handleUploadedFiles(files);
			return true;
		}
	}

	declare state: { status?: string, bgUrl?: string };

	setBg = (ev: Event) => {
		let curtarget = ev.currentTarget as HTMLButtonElement;
		let bg = curtarget.value;
		if (bg === 'custom') {
			PSBackground.set(this.props.room.args?.bgUrl as string || '', 'custom');
			this.close();
		} else {
			PSBackground.set('', bg);
		}
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.forceUpdate();
	};

	static handleUploadedFiles(files: FileList | null | undefined, skipConfirm?: boolean) {
		if (!files?.[0]) return;

		const file = files[0];
		const reader = new FileReader();

		reader.onload = () => {
			const bgUrl = reader.result as string;
			if (bgUrl.length > 4200000) {
				PS.join('changebackground' as RoomID, {
					args: { error: trUI("Image is too large and can't be saved. It should be under 3.5MB or so.") },
				});
				return;
			}
			if (skipConfirm) {
				PSBackground.set(bgUrl, 'custom');
			} else {
				PS.join('changebackground' as RoomID, {
					args: { bgUrl },
				});
			}
			PS.rooms['changebackground']?.update(null);
		};

		reader.onerror = () => {
			PS.join('changebackground' as RoomID, {
				args: { error: trUI("Failed to load background image.") },
			});
		};
		reader.readAsDataURL(file);
	}

	uploadBg = (ev: Event) => {
		this.setState({ status: undefined });
		const input = this.base?.querySelector<HTMLInputElement>('input[name=bgfile]');
		BackgroundListPanel.handleUploadedFiles(input?.files, true);
		ev.preventDefault();
		ev.stopImmediatePropagation();
	};

	renderUpload() {
		const room = this.props.room;
		if (room.args?.error) {
			return <PSPanelWrapper room={room} width={480}><div class="pad">
				<p class="error">{room.args.error}</p>
				<p class="buttonbar">
					<button data-cmd="/close" class="button"><strong>{trUI("Done")}</strong></button>
				</p>
			</div></PSPanelWrapper>;
		}

		if (room.args?.bgUrl) {
			return <PSPanelWrapper room={room} width={480}><div class="pad">
				<p>
					<img src={room.args.bgUrl as string} style="display:block;margin:auto;max-width:90%;max-height:500px" />
				</p>
				<p class="buttonbar">
					<button onClick={this.setBg} value="custom" class="button"><strong>{trUI("Set as background")}</strong></button> {}
					<button data-cmd="/close" class="button">{trUI("Cancel")}</button>
				</p>
			</div></PSPanelWrapper>;
		}

		return null;
	}

	override render() {
		const room = this.props.room;
		const option = (val: string) => val === PSBackground.id ? 'option cur' : 'option';
		return this.renderUpload() || <PSPanelWrapper room={room} width={480}><div class="pad">
			<p><strong>{trUI("Default")}</strong></p>
			<div class="bglist">
				<button onClick={this.setBg} value="" class={option('')}>
					<strong
						style="
						background: #888888;
						color: white;
						padding: 16px 18px;
						display: block;
						font-size: 12pt;
					"
					>{location.host === Config.routes.client ? trUI("Random") : trUI("Server")}</strong>
				</button>
			</div>
			<div style="clear: left"></div>
			<p><strong>{trUI("Official")}</strong></p>
			<div class="bglist">
				<button onClick={this.setBg} value="charizards" class={option('charizards')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-mewshiny.jpg?v=20260826hard1);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("Mew shiny")}
				</button>
				<button onClick={this.setBg} value="horizon" class={option('horizon')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-starters.jpg?v=20260826hard1);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("Starters")}
				</button>
				<button onClick={this.setBg} value="ocean" class={option('ocean')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-greninja.jpg?v=20260826hard1);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("Greninja vs Charizard")}
				</button>
				<button onClick={this.setBg} value="shaymin" class={option('shaymin')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-fantasmas.jpg?v=20260826hard1);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("Fantasmas")}
				</button>
				<button onClick={this.setBg} value="lastsupper" class={option('lastsupper')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-lastsupper.jpg?v=20260826hard1);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("La Última Cena")}
				</button>
				<button onClick={this.setBg} value="solidblue" class={option('solidblue')}>
					<span
						class="bg"
						style="background-image:url(/client/img/bg-megamewtwo.jpg?v=20260826hard3);background-size:cover;background-position:center;background-repeat:no-repeat"
					></span>{}
					{trUI("Mega Mewtwo")}
				</button>
			</div>
			<div style="clear: left"></div>
			<p><strong>{trUI("Custom")}</strong></p>
			<p>
				{trUI("Upload:")}
			</p>
			<p><input type="file" accept="image/*" name="bgfile" onChange={this.uploadBg} /></p>
			{!!this.state.status && <p class="error">{this.state.status}</p>}
			<p class="buttonbar">
				<button data-cmd="/close" class="button"><strong>{trUI("Done")}</strong></button>
			</p>
		</div>
		</PSPanelWrapper>;
	}
}

class ChatFormattingPanel extends PSRoomPanel {
	static readonly id = 'chatformatting';
	static readonly routes = ['chatformatting'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	handleOnChange = (ev: Event) => {
		const setting = "hide" + (ev.currentTarget as HTMLInputElement).name;
		const value = (ev.currentTarget as HTMLInputElement).checked;
		let curPref = PS.prefs.chatformatting;
		curPref[setting] = value;
		PS.prefs.set("chatformatting", curPref);
		ev.preventDefault();
		ev.stopImmediatePropagation();
	};

	override render() {
		const room = this.props.room;
		const ctrl = PSView.isMac ? trUI("Cmd") : trUI("Ctrl");
		return <PSPanelWrapper room={room} width={480}><div class="pad">
			<p>{trUI("Usable formatting:")}</p>
			<p>**<strong>{trUI("bold")}</strong>** (<kbd>{ctrl}</kbd>+<kbd>B</kbd>)</p>
			<p>__<em>{trUI("italics")}</em>__ (<kbd>{ctrl}</kbd>+<kbd>I</kbd>)</p>
			<p>``<code>{trUI("code formatting")}</code>`` (<kbd>{trUI("Ctrl")}</kbd>+<kbd>`</kbd>)</p>
			<p>~~<s>{trUI("strikethrough")}</s>~~</p>
			<p>^^<sup>{trUI("superscript")}</sup>^^</p>
			<p>\\<sub>{trUI("subscript")}</sub>\\</p>
			<p>
				<label class="checkbox">
					<input
						onChange={this.handleOnChange}
						type="checkbox"
						name="greentext"
						checked={PS.prefs.chatformatting.hidegreentext}
					/> {trUI("Suppress")} <span class="greentext">&gt;greentext</span>
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						onChange={this.handleOnChange}
						type="checkbox"
						name="me"
						checked={PS.prefs.chatformatting.hideme}

					/> {trUI("Suppress")} <code>/me</code> <em>{trUI("action formatting")}</em>
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						onChange={this.handleOnChange}
						type="checkbox"
						name="spoiler"
						checked={PS.prefs.chatformatting.hidespoiler}
					/> {trUI("Auto-show spoilers:")} <span class="spoiler">{trUI("these things")}</span>
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						onChange={this.handleOnChange}
						type="checkbox"
						name="links"
						checked={PS.prefs.chatformatting.hidelinks}
					/> {trUI("Make [[clickable links]] unclickable")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						onChange={this.handleOnChange}
						type="checkbox"
						name="interstice"
						checked={PS.prefs.chatformatting.hideinterstice}
					/> {trUI("Don't warn for untrusted links")}
				</label>
			</p>
			<p><button data-cmd="/close" class="button">{trUI("Done")}</button></p>
		</div>
		</PSPanelWrapper>;
	}
}

class LeaveRoomPanel extends PSRoomPanel {
	static readonly id = 'confirmleaveroom';
	static readonly routes = ['confirmleaveroom'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	override render() {
		const room = this.props.room;
		const parentRoomid = room.parentRoomid!;

		return <PSPanelWrapper room={room} width={480}><div class="pad">
			<p>{trUI("Close")} <code>{parentRoomid || "ERROR"}</code>?</p>
			<p class="buttonbar">
				<button data-cmd={`/closeand /close ${parentRoomid}`} class="button autofocus">
					<strong>{trUI("Close Room")}</strong>
				</button> {}
				<button data-cmd="/close" class="button">
					{trUI("Cancel")}
				</button>
			</p>
		</div></PSPanelWrapper>;
	}
}
class BattleOptionsPanel extends PSRoomPanel {
	static readonly id = 'battleoptions';
	static readonly routes = ['battleoptions'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	handleHardcoreMode = (ev: Event) => {
		const mode = (ev.currentTarget as HTMLInputElement).checked;
		const room = this.getBattleRoom();
		if (!room) return this.close();

		room.battle.setHardcoreMode(mode);
		if (mode) {
			room.add(`||` + trUI("Hardcore mode ON: Information not available in-game is now hidden."));
		} else {
			room.add(`||` + trUI("Hardcore mode OFF: Information not available in-game is now shown."));
		}
		room.update(null);
	};
	handleIgnoreSpectators = (ev: Event | boolean) => {
		const value = typeof ev === "object" ?
			(ev.currentTarget as HTMLInputElement).checked :
			ev;
		const room = this.getBattleRoom();
		if (!room) return this.close();

		room.battle.ignoreSpects = value;
		room.add(`||` + (room.battle.ignoreSpects ? trUI("Spectators ignored.") : trUI("Spectators no longer ignored.")));
		const chats = document.querySelectorAll<HTMLElement>('.battle-log .chat');
		const displaySetting = room.battle.ignoreSpects ? 'none' : '';
		for (const chat of chats) {
			const small = chat.querySelector('small');
			if (!small) continue;
			const text = small.innerText;
			const isPlayerChat = text.includes('\u2606') || text.includes('\u2605');
			if (!isPlayerChat) {
				chat.style.display = displaySetting;
			}
		}
		room.battle.scene.log.updateScroll();
	};
	handleIgnoreOpponent = (ev: Event | boolean) => {
		const value = typeof ev === "object" ?
			(ev.currentTarget as HTMLInputElement).checked :
			ev;
		const room = this.getBattleRoom();
		if (!room) return this.close();

		room.battle.ignoreOpponent = value;
		room.battle.resetToCurrentTurn();
	};
	handleIgnoreNicks = (ev: Event | boolean) => {
		const value = typeof ev === "object" ?
			(ev.currentTarget as HTMLInputElement).checked :
			ev;
		const room = this.getBattleRoom();
		if (!room) return this.close();

		room.battle.ignoreNicks = value;
		room.battle.resetToCurrentTurn();
	};
	handleAllSettings = (ev: Event) => {
		const setting = (ev.currentTarget as HTMLInputElement).name;
		const value = (ev.currentTarget as HTMLInputElement).checked;
		const room = this.getBattleRoom();

		switch (setting) {
		case 'autotimer': {
			PS.prefs.set('autotimer', value);
			if (value) {
				room?.send('/timer on');
			}
			break;
		}
		case 'autohardcore': {
			PS.prefs.set('autohardcore', value);
			if (room?.battle) {
				room.battle.setHardcoreMode(value);
				room.update(null);
			}
			break;
		}
		case 'spectatefromstart': {
			PS.prefs.set('spectatefromstart', value);
			break;
		}
		case 'ignoreopp': {
			PS.prefs.set('ignoreopp', value);
			this.handleIgnoreOpponent(value);
			break;
		}
		case 'ignorespects': {
			PS.prefs.set('ignorespects', value);
			this.handleIgnoreSpectators(value);
			break;
		}
		case 'ignorenicks': {
			PS.prefs.set('ignorenicks', value);
			this.handleIgnoreNicks(value);
			break;
		}
		case 'rightpanel': {
			PS.prefs.set('rightpanelbattles', value);
			break;
		}
		case 'disallowspectators': {
			PS.prefs.set('disallowspectators', value);
			PS.mainmenu.disallowSpectators = value;
			break;
		}
		}
	};
	handleBattleLayout = (ev: Event) => {
		const value = (ev.currentTarget as HTMLSelectElement).value as typeof PS.prefs.battlelayout;
		PS.prefs.set('battlelayout', value || null);
		PS.update();
	};
	getBattleRoom() {
		const battleRoom = this.props.room.getParent() as BattleRoom | null;
		return battleRoom?.battle ? battleRoom : null;
	}

	override render() {
		const room = this.props.room;
		const battleRoom = this.getBattleRoom();
		const isPlaying = !!battleRoom?.isPlaying();
		const canOfferTie = battleRoom && ((battleRoom.battle.turn >= 100 && isPlaying) || PS.user.group === '~');
		let automaticLayout: BattleLayoutPreference | null = null;
		if (battleRoom) {
			const { layout, overlayControls } = PS.chooseBattleLayout(battleRoom.width, battleRoom.height);
			automaticLayout = `${layout}${overlayControls ? '-overlay' : ''}` as BattleLayoutPreference;
		}
		return <PSPanelWrapper room={room} width={380}><div class="pad">
			{battleRoom && <>
				<p><strong>{trUI("In this battle")}</strong></p>
				<p class="buttonbar">
					<button data-cmd="/closeand /inopener /forfeit" class="button" disabled={!isPlaying}>
						{trUI("Forfeit")}
					</button> {}
					<button data-cmd="/closeand /inopener /offertie" class="button" disabled={!canOfferTie}>
						{trUI("Offer tie")} {!canOfferTie && trUI("(turn 100+)")}
					</button>
				</p>
				<p>
					<label class="checkbox">
						<input
							checked={battleRoom.battle.hardcoreMode}
							type="checkbox" onChange={this.handleHardcoreMode}
						/> {trUI("Hardcore mode (hide info not shown in-game)")}
					</label>
				</p>
				<p>
					<label class="checkbox">
						<input
							checked={battleRoom.battle.ignoreSpects}
							type="checkbox" onChange={this.handleIgnoreSpectators}
						/> {trUI("Ignore spectators")}
					</label>
				</p>
				<p>
					<label class="checkbox">
						<input
							checked={battleRoom.battle.ignoreOpponent}
							type="checkbox" onChange={this.handleIgnoreOpponent}
						/> {trUI("Ignore opponent")}
					</label>
				</p>
				<p>
					<label class="checkbox">
						<input
							checked={battleRoom.battle?.ignoreNicks}
							type="checkbox" onChange={this.handleIgnoreNicks}
						/> {trUI("Ignore nicknames")}
					</label>
				</p>
			</>}
			<p><strong>{trUI("All battles")}</strong></p>
			<p>
				<label class="optlabel">{trUI("Layout:")} <select
					name="battlelayout" class="select" onChange={this.handleBattleLayout}
					value={PS.prefs.battlelayout || ''}
				>
					<option value="">
						{trUI("Automatic")}{automaticLayout ? ` (${battleLayoutLabel(automaticLayout)})` : ''}
					</option>
					<option value="side-by-side">
						{battleLayoutLabel('side-by-side')} {trUI("(DESKTOP)")}
					</option>
					<option value="side-by-side-overlay">
						{battleLayoutLabel('side-by-side-overlay')}
					</option>
					<option value="top-and-bottom">
						{battleLayoutLabel('top-and-bottom')} {trUI("(MOBILE VERTICAL)")}
					</option>
					<option value="top-and-bottom-overlay">
						{battleLayoutLabel('top-and-bottom-overlay')}
					</option>
					<option value="scrolling">
						{battleLayoutLabel('scrolling')}
					</option>
					<option value="scrolling-overlay">
						{battleLayoutLabel('scrolling-overlay')} {trUI("(MOBILE HORIZONTAL)")}
					</option>
				</select></label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="disallowspectators" checked={PS.prefs.disallowspectators || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> <abbr title={trUI("You can still invite spectators by giving them the URL or using the /invite command")}>{trUI("Invite only (hide from Battles list)")}</abbr>
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="ignorenicks" checked={PS.prefs.ignorenicks || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Ignore Pokémon nicknames")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="ignorespects" checked={PS.prefs.ignorespects || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Ignore spectators")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="ignoreopp" checked={PS.prefs.ignoreopp || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Ignore opponent")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="autotimer" checked={PS.prefs.autotimer || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Automatically start timer")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="autohardcore" checked={PS.prefs.autohardcore || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Hardcore mode")}
				</label>
			</p>
			<p>
				<label class="checkbox">
					<input
						name="spectatefromstart" checked={!!PS.prefs.spectatefromstart}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Start at turn 0 when spectating battles")}
				</label>
			</p>
			{!PS.prefs.onepanel && window.innerWidth >= 800 && <p>
				<label class="checkbox">
					<input
						name="rightpanel" checked={PS.prefs.rightpanelbattles || false}
						type="checkbox" onChange={this.handleAllSettings}
					/> {trUI("Open new battles in the right-side panel")}
				</label>
			</p>}
			<p class="buttonbar">
				<button data-cmd="/close" class="button">{trUI("Done")}</button> {}
			</p>
		</div>
		</PSPanelWrapper>;
	}
}

class PopupRoom extends PSRoom {
	returnValue: unknown = this.args?.cancelValue;
	override destroy() {
		(this.args?.callback as any)?.(this.returnValue);
		super.destroy();
	}
}

class PopupPanel extends PSRoomPanel<PopupRoom> {
	static readonly id = 'popup';
	static readonly routes = ['popup-*'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;
	static readonly Model = PopupRoom;

	handleSubmit = (ev: Event) => {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const room = this.props.room;
		room.returnValue = room.args?.okValue;
		const textbox = this.base!.querySelector<HTMLInputElement>('input[name=value]');
		if (textbox) {
			room.returnValue = textbox.value;
		}
		this.close();
	};
	override componentDidMount() {
		super.componentDidMount();
		const textbox = this.base!.querySelector<HTMLInputElement>('input[name=value]');
		if (!textbox) return;
		textbox.value = this.props.room.args?.value as string || '';
		textbox.select();
	}
	parseMessage(message: string) {
		let html = false;
		while (true) {
			if (message.startsWith('|modal|')) {
				message = message.slice(7);
				this.props.room.closable = false;
				continue;
			}
			if (message.startsWith('|html|')) {
				message = message.slice(6);
				html = true;
				continue;
			}
			if (message.startsWith('|wide|')) {
				message = message.slice(6);
				continue;
			}
			break;
		}
		if (html) return BattleLog.sanitizeHTML(message);
		return BattleLog.parseMessage(message);
	}

	override render() {
		const room = this.props.room;
		const okButton = room.args?.okButton as string || trUI("OK");
		const cancelButton = room.args?.cancelButton as string | undefined;
		const otherButtons = room.args?.otherButtons as preact.ComponentChildren;
		const value = room.args?.value as string | undefined;
		let type = (room.args?.type || (typeof value === 'string' ? 'text' : null)) as string | null;
		const inputMode = type === 'numeric' ? 'numeric' : undefined;
		if (type === 'numeric') type = 'text';
		const message = room.args?.message;
		return <PSPanelWrapper room={room} width={room.args?.width as number || 480}>
			<form class="pad" onSubmit={this.handleSubmit}>
				{message && <div
					style="white-space:pre-wrap;word-wrap:break-word"
					dangerouslySetInnerHTML={{ __html: this.parseMessage(message as string || '') }}
				></div>}
				{!!type && <p><label class="label">{room.args?.label as string || ''} <input
					name="value" type={type} inputMode={inputMode} class="textbox autofocus" style="width:100%;box-sizing:border-box"
				/></label></p>}
				<p class="buttonbar">
					<button class={`button${!type ? ' autofocus' : ''}`} type="submit" style="min-width:50px">
						<strong>{okButton}</strong>
					</button> {}
					{otherButtons} {}
					{!!cancelButton && <button class="button" data-cmd="/close" type="button">
						{cancelButton}
					</button>}
				</p>
			</form>
		</PSPanelWrapper>;
	}
}

class RoomTabListPanel extends PSRoomPanel {
	static readonly id = 'roomtablist';
	static readonly routes = ['roomtablist'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;

	startingLayout = PS.prefs.onepanel;
	handleLayoutChange = (ev: Event) => {
		const checkbox = ev.currentTarget as HTMLInputElement;
		PS.prefs.onepanel = checkbox.checked ? 'vertical' : this.startingLayout;
		PS.update();
	};
	override render() {
		const verticalTabs = PS.prefs.onepanel === 'vertical';
		return <PSPanelWrapper room={this.props.room}><div class="tablist">
			<ul>
				{PS.leftRoomList.map(roomid => PSHeader.renderRoomTab(roomid, true))}
			</ul>
			<ul>
				{PS.rightRoomList.map(roomid => PSHeader.renderRoomTab(roomid, true))}
			</ul>
			<div class="pad"><label class="checkbox"><input
				type="checkbox" checked={verticalTabs} onChange={this.handleLayoutChange}
			/> {trUI("Try vertical tabs")}</label></div>
		</div></PSPanelWrapper>;
	}
}
class BattleTimerPanel extends PSRoomPanel {
	static readonly id = 'battletimer';
	static readonly routes = ['battletimer'];
	static readonly location = 'popup';
	static readonly noURL = true;

	override render() {
		const room = this.props.room.getParent() as BattleRoom;
		return <PSPanelWrapper room={this.props.room}><div class="pad">
			{room.battle.kickingInactive ? (
				<button class="button" data-cmd="/closeand /inopener /timer stop">{trUI("Stop Timer")}</button>
			) : (
				<button class="button" data-cmd="/closeand /inopener /timer start">{trUI("Start Timer")}</button>
			)}
		</div>
		</PSPanelWrapper>;
	}
}

class RulesPanel extends PSRoomPanel<PopupRoom> {
	static readonly id = 'rules';
	static readonly routes = ['rules-*'];
	static readonly location = 'modal-popup';
	static readonly noURL = true;
	static readonly Model = PopupRoom;
	declare state: { canClose?: boolean | null, timeLeft?: number | null, timerRef?: any };

	override componentDidMount() {
		super.componentDidMount();
		const room = this.props.room;
		const isWarn = room.args?.type === 'warn';
		if (isWarn) {
			room.closable = false;
			const timerRef = setInterval(() => {
				const timeLeft = this.state.timeLeft!;
				if (timeLeft === 1) {
					clearInterval(this.state.timerRef);
					this.setState({ canClose: true, timeLeft: 0, timerRef: null });
				} else {
					this.setState({ canClose: false, timeLeft: timeLeft - 1 });
				}
			}, 1000);
			this.setState({ timeLeft: WARNING_SECONDS, canClose: false, timerRef });
		}
	}

	override componentWillUnmount(): void {
		if (this.state.timerRef) {
			clearInterval(this.state.timerRef);
		}
	}

	override render() {
		const room = this.props.room;
		const type = room.args?.type;
		const isWarn = type === 'warn';
		const message = room.args?.message as string || '';
		return <PSPanelWrapper room={room} width={room.args?.width as number || 780}>
			<div class="pad">
				{
					isWarn &&
					<p><strong style="color:red">{(BattleLog.escapeHTML(message) || trUI("You have been warned for breaking the rules."))}
					</strong></p>
				}
				<h2>{trUI("Pokémon Showdown Rules")}</h2>
				<p><b>1.</b> {trUI("Be nice to people. Respect people. Don't be rude or mean to people.")}</p>
				<p><b>2.</b> {}
					{trUI("Follow US laws (PS is based in the US). No porn (minors use PS), don't distribute pirated material, and don't slander others.")}</p>
				<p><b>3.</b> {}
					&nbsp;{trUI("No sex. Don't discuss anything sexually explicit, not even in private messages, not even if you're both adults.")}</p>
				<p><b>4.</b> {}
					&nbsp;{trUI("No cheating. Don't exploit bugs to gain an unfair advantage. Don't game the system (by intentionally losing against yourself or a friend in a ladder match, by timerstalling, etc). Don't impersonate staff if you're not.")}</p>
				<p><b>5.</b> {}
					{trUI("Moderators have discretion to punish any behaviour they deem inappropriate, whether or not it's on this list. If you disagree with a moderator ruling, appeal to an administrator (a user with ~ next to their name) or")} {}
					<a href="https://pokemonshowdown.com/appeal">{trUI("Discipline Appeals")}</a>.</p>
				<p>{trUI("(Note: The First Amendment does not apply to PS, since PS is not a government organization.)")}</p>
				<p><b>{trUI("Chat")}</b></p>
				<p><b>1.</b> {}
					{trUI("Do not spam, flame, or troll. This includes advertising, raiding, asking questions with one-word answers in the lobby, and flooding the chat such as by copy/pasting logs in the lobby.")}</p>
				<p><b>2.</b> {}
					{trUI("Don't call unnecessary attention to yourself. Don't be obnoxious. ALL CAPS and")} <i>{trUI("formatting")}</i> {}
					{trUI("are acceptable to emphasize things, but should be used sparingly, not all the time.")}</p>
				<p><b>3.</b> {}
					{trUI("No minimodding: don't mod if it's not your job. Don't tell people they'll be muted, don't ask for people to be muted, and don't talk about whether or not people should be muted ('inb4 mute', etc). This applies to bans and other punishments, too.")}</p>
				<p><b>4.</b> {}
					{trUI("We reserve the right to tell you to stop discussing moderator decisions if you become unreasonable or belligerent")}</p>
				<p><b>5.</b> {trUI("English only, unless specified otherwise.")}</p>
				<p>{trUI("(Note: You can opt out of chat rules in private chat rooms and battle rooms, but only if all ROs or players agree to it.)")}</p>
				{
					!isWarn && <>
						<p><b>{trUI("Usernames")}</b></p>
						<p>{trUI("Your username can be chosen and changed at any time. Keep in mind:")}</p>
						<p><b>1.</b> {trUI("Usernames may not impersonate a recognized user (a user with %, @, #, or ~ next to their name) or a famous person/organization that uses PS or is associated with Pokémon.")}</p>
						<p><b>2.</b> {trUI("Usernames may not be derogatory or insulting in nature, to an individual or group (insulting yourself is okay as long as it's not too serious).")}</p>
						<p><b>3.</b> {trUI("Usernames may not directly reference sexual activity, or be excessively disgusting.")}</p>
						<p>{trUI('This policy is less restrictive than that of many places, so you might see some "borderline" nicknames that might not be accepted elsewhere. You might consider it unfair that they are allowed to keep their nickname. The fact remains that their nickname follows the above rules, and if you were asked to choose a new name, yours does not.')}</p>
					</>
				}
				<p class="buttonbar"><button
					name="close"
					data-cmd="/close" class="button autofocus"
					disabled={!this.state.canClose}
				> {trUI("Close")} {this.state.timerRef && <>({this.state.timeLeft} {trUI("sec")})</>}</button></p>
			</div>
		</PSPanelWrapper>;
	}
}

PS.addRoomType(
	UserPanel,
	UserOptionsPanel,
	UserListPanel,
	VolumePanel,
	OptionsPanel,
	LoginPanel,
	AvatarsPanel,
	ChangePasswordPanel,
	RegisterPanel,
	BattleForfeitPanel,
	ReplacePlayerPanel,
	BackgroundListPanel,
	LeaveRoomPanel,
	ChatFormattingPanel,
	PopupPanel,
	RoomTabListPanel,
	BattleOptionsPanel,
	BattleTimerPanel,
	RulesPanel
);
